// Static content for the /our-core-technology page.
// Source: "Landing Page - Our Core Technology.docx".

export const SEO_META = {
  title: "AI Vehicle Inspection Technology | Inspektlabs",
  description:
    "Explore Inspektlabs' AI vehicle inspection technology for automated damage detection, photo and video analysis, and scalable vehicle inspections.",
};

export const HERO = {
  tag: "Core Technology",
  title: "AI-Powered Vehicle Inspection Technology Built for Real-World Damage",
  body: "Trained on 50M+ real-world vehicle images and videos, Inspektlabs uses computer vision and neural networks to analyse vehicle images and videos, identify damage, and turn visual inspections into structured inspection data. Built to handle real-world inspection conditions, our technology works across different vehicle types, camera setups, lighting conditions, and inspection environments.",
  primaryCta: "Request a Demo",
  secondaryCta: "Explore AI Vehicle Damage Detection",
  secondaryLink: "/damage-detection",
};

// "How AI Vehicle Inspection Technology Works" — blue panel with 4 illustration
// circles + short captions.
export const HOW_IT_WORKS = {
  title: "The Technology Behind Automated Vehicle Inspections",
  body: "Real-world vehicle inspections rarely follow controlled conditions. Changes in lighting, camera angles, image quality and vehicle condition can affect how damage is captured and assessed. Inspektlabs combines trained AI models, edge processing and image and video analysis to handle these variations and support automated vehicle damage detection.",
  items: [
    {
      icon: "neuralnet",
      title: "Custom Neural Networks",
      body: "Trained on 50M+ damaged vehicle images, our neural networks are built to identify vehicle damage across varied inspection conditions.",
    },
    {
      icon: "chip",
      title: "Edge-Deployed AI Models",
      body: "Edge-deployed AI models enable real-time vehicle inspection feedback, helping inspection teams assess vehicle condition during the capture process.",
    },
    {
      icon: "dualmedia",
      title: "Dual Input Support",
      body: "The platform processes both vehicle photos and videos, supporting different inspection workflows and capture environments.",
    },
    {
      icon: "training",
      title: "Real-World Training",
      body: "Models trained on real-world vehicle damage scenarios help automate damage assessment and support repair-versus-replace decisions.",
    },
  ],
};

// "The Technology Behind Automated Vehicle Inspections" — intro + numbered cards.
export const TECHNOLOGY = {
  title: "The Technology Behind Automated Vehicle Inspections",
  body: [
    {
      text: "Real-world vehicle inspections rarely follow controlled conditions. Changes in lighting, camera angles, image quality and vehicle condition can affect how damage is captured and assessed. Inspektlabs combines trained AI models, edge processing and ",
    },
    { text: "image and video analysis", link: "/photo-video-capture" },
    { text: " to handle these variations and support automated " },
    { text: "vehicle damage detection", link: "/damage-detection" },
    { text: "." },
  ],
  cards: [
    {
      no: "01",
      title: "Custom Neural Networks",
      body: "Trained on 50M+ damaged vehicle images, our neural networks are built to identify vehicle damage across varied inspection conditions.",
    },
    {
      no: "02",
      title: "Edge-Deployed AI Models",
      body: "Edge-deployed AI models enable real-time vehicle inspection feedback, helping inspection teams assess vehicle condition during the capture process.",
    },
    {
      no: "03",
      title: "Dual Input Support",
      body: "The platform processes both vehicle photos and videos, supporting different inspection workflows and capture environments.",
    },
    {
      no: "04",
      title: "Real-World Training",
      body: "Models trained on real-world vehicle damage scenarios help automate damage assessment and support repair-versus-replace decisions.",
    },
  ],
};

// "Secure, Scalable Infrastructure" — two columns of points.
export const INFRASTRUCTURE = {
  title: "Secure, Scalable Infrastructure for Global Vehicle Inspections",
  body: "Our digital vehicle inspection platform runs on resilient cloud infrastructure built for enterprise security, scalability and global deployment. From data protection and privacy to regional infrastructure and secure data transmission, the platform is designed to support vehicle inspection workflows at scale.",
  columns: [
    {
      heading: "Security & Compliance",
      icon: "shield",
      points: [
        {
          icon: "eyeOff",
          title: "Automatically masks faces, license plates, VIN and GPS before processing",
          body: "Personally identifiable and sensitive information can be automatically detected and masked before inspection images are processed.",
        },
        {
          icon: "certificate",
          title: "ISO 27001 certified and GDPR compliant",
          body: "Built around established information-security and data-protection practices to support enterprise and regulatory requirements.",
        },
        {
          icon: "lock",
          title: "Privacy by Design: no client data stored for AI training",
          body: "Customer inspection data is not used to train AI models, helping organisations maintain control over their inspection data.",
        },
        {
          icon: "cloud",
          title: "Multi-cloud hosted on AWS and Azure",
          body: "Cloud infrastructure across AWS and Azure provides the flexibility and resilience required for enterprise vehicle inspection deployments.",
        },
      ],
    },
    {
      heading: "Global Infrastructure",
      icon: "globe",
      points: [
        {
          icon: "server",
          title: "Virtual Private Cloud for isolated network security",
          body: "Virtual private cloud infrastructure provides an isolated environment for secure application and data processing.",
        },
        {
          icon: "activity",
          title: "Load balancing handles millions of mobile vehicle inspection requests",
          body: "Load-balancing infrastructure helps distribute inspection traffic and maintain reliable performance as request volumes increase.",
        },
        {
          icon: "mapPin",
          title: "Regional deployment in 30+ countries for low latency",
          body: "Regional infrastructure supports faster access and more responsive inspection workflows for users across different markets.",
        },
        {
          icon: "shieldCheck",
          title: "Data encrypted in transit",
          body: "Inspection data is encrypted while moving between users, applications and infrastructure to protect information during transmission.",
        },
      ],
    },
  ],
};

// "Why Choose Inspektlabs' Vehicle Inspection Technology?" — 5 cards.
export const WHY_CHOOSE = {
  title: "Why Choose Inspektlabs' Vehicle Inspection Technology?",
  body: [
    {
      text: "Built for high-volume vehicle inspection operations, Inspektlabs combines AI-based ",
    },
    { text: "vehicle damage detection", link: "/damage-detection", newTab: true },
    {
      text: " with image and video analysis to deliver faster, more consistent inspection results. The technology supports different vehicle types, inspection workflows and integration requirements, helping organisations bring automated inspection into their existing operations.",
    },
  ],
  cards: [
    {
      icon: "video",
      title: "Photo & Video AI Inspection",
      body: "Process both photos and videos for automated vehicle inspections, supported by photo and video capture designed for different inspection workflows and environments.",
      link: "/photo-video-capture",
      linkText: "Explore photo & video capture",
    },
    {
      icon: "target",
      title: "Up to 90% Accuracy",
      body: "Validated by automotive players and trained on 50M+ vehicle images to support consistent damage detection across real-world inspection conditions.",
      link: "/damage-detection",
      linkText: "Explore AI damage detection",
    },
    {
      icon: "code",
      title: "Easy API Integration",
      body: "Integrate AI vehicle inspection into existing automotive, insurance, and fleet platforms through APIs, so inspection results fit into your existing workflows.",
      link: "/fleet-management",
      linkText: "Explore fleet inspection integration",
    },
    {
      icon: "layers",
      title: "163+ Parts Analysed",
      body: "Analyse damage at the individual part level across 163+ vehicle parts, from exterior components to interior parts.",
      link: "/damage-detection",
      linkText: "Learn about AI vehicle part detection",
    },
    {
      icon: "truck",
      title: "Multiple Vehicle Types",
      body: "Support inspections across passenger and commercial vehicles, with solutions designed for different automotive and fleet use cases.",
      link: "/truck-inspection-software",
      linkText: "Explore truck inspection software",
    },
  ],
};

export const FAQ_ITEMS = [
  {
    Q: "Which cloud platforms does Inspektlabs use to host its vehicle inspection services?",
    A: [
      "Inspektlabs uses AWS and Microsoft Azure to host its vehicle inspection services. Regional deployment options are available to support customers across different markets.",
    ],
  },
  {
    Q: "What camera setup is required for Inspektlabs' automated vehicle inspections?",
    A: [
      "Inspektlabs supports both smartphone cameras and fixed camera setups. The right setup depends on the inspection workflow, capture environment and level of automation required.",
    ],
  },
  {
    Q: "How does Inspektlabs ensure data security and privacy for vehicle inspections?",
    A: [
      "Inspektlabs uses measures such as PII detection and masking, encryption in transit and controlled data processing to protect inspection data. Sensitive information, including faces and license plates, can be masked before processing.",
    ],
  },
  {
    Q: "What AI models does Inspektlabs use for vehicle damage detection?",
    A: [
      "Inspektlabs uses an ensemble of object detection and segmentation models for vehicle damage detection. The models are trained on real-world vehicle damage data to identify and localise damage across different vehicle parts.",
    ],
  },
  {
    Q: "Can Inspektlabs analyse both vehicle photos and videos?",
    A: [
      "Yes. Inspektlabs analyses both vehicle photos and videos for automated inspection and damage detection. This gives teams the flexibility to use the capture method that fits their inspection process.",
    ],
  },
  {
    Q: "How does Inspektlabs handle sensitive information in vehicle inspection images?",
    A: [
      "Inspektlabs can detect and mask sensitive information such as faces and license plates in inspection images. This helps reduce the amount of personally identifiable information included in the data being processed.",
    ],
  },
];

export const CLOSING_CTA = {
  title: "See AI Vehicle Inspection in Action",
  body: "Explore how Inspektlabs can automate vehicle inspections, from damage detection to inspection reporting.",
  cta: "Request a Demo",
};
